import type { AgentTool, ToolContext, HostBridge, UserQuestion } from './tools';

export type { HostBridge, UserQuestion } from './tools';

/**
 * `AskUserQuestion` tool — lets the model pose a structured choice to a human.
 * Delegates to `ctx.host.ask`. Registered only when a HostBridge is provided
 * (autonomous runs don't see it), so it never dead-ends a headless agent.
 */
export const askUserQuestionTool: AgentTool = {
  name: 'AskUserQuestion',
  description:
    'Ask the user a multiple-choice question when a decision is genuinely theirs to make (ambiguous requirement, a fork you cannot resolve from context). Returns their selection.',
  parameters: {
    type: 'object',
    required: ['question', 'options'],
    properties: {
      question: { type: 'string' },
      header: { type: 'string', description: 'short label for the question' },
      options: {
        type: 'array',
        items: { type: 'object', required: ['label'], properties: { label: { type: 'string' }, description: { type: 'string' } } },
      },
      multiSelect: { type: 'boolean' },
    },
  },
  async run(args: UserQuestion, ctx: ToolContext) {
    if (!ctx.host?.ask) {
      const fallback = args.options?.[0]?.label ?? '';
      return `No interactive user is available — proceed using your best judgment.${fallback ? ` (Closest default: "${fallback}")` : ''}`;
    }
    const answer = ctx.host.ask(args); // human-blocking — park it so the wait doesn't count toward the timeout
    return ctx.parkHuman ? ctx.parkHuman(answer) : answer;
  },
};

/** Deterministic HostBridge for tests/dev: scripts answers + records what was asked. */
export class ScriptedHostBridge implements HostBridge {
  public asked: UserQuestion[] = [];
  public confirms: string[] = [];
  public events: Array<{ kind: string; message: string; data?: unknown }> = [];
  constructor(private answers: string[] = [], private confirmDefault = true) {}
  async ask(q: UserQuestion): Promise<string> {
    this.asked.push(q);
    return this.answers.shift() ?? q.options[0]?.label ?? '';
  }
  async confirm(prompt: string): Promise<boolean> {
    this.confirms.push(prompt);
    return this.confirmDefault;
  }
  notify(event: { kind: string; message: string; data?: unknown }): void {
    this.events.push(event);
  }
}

/** Node CLI HostBridge: prompts on stdin. (node-only host adapter; not edge-safe by design.) */
export class ConsoleHostBridge implements HostBridge {
  async ask(q: UserQuestion): Promise<string> {
    const rl = await import('node:readline/promises');
    const io = rl.createInterface({ input: process.stdin, output: process.stdout });
    try {
      const lines = [q.header ? `\n[${q.header}] ${q.question}` : `\n${q.question}`];
      q.options.forEach((o, i) => lines.push(`  ${i + 1}) ${o.label}${o.description ? ' — ' + o.description : ''}`));
      const ans = await io.question(lines.join('\n') + '\n> ');
      const n = Number(ans.trim());
      return Number.isInteger(n) && q.options[n - 1] ? q.options[n - 1].label : ans.trim();
    } finally {
      io.close();
    }
  }
  async confirm(prompt: string): Promise<boolean> {
    const rl = await import('node:readline/promises');
    const io = rl.createInterface({ input: process.stdin, output: process.stdout });
    try {
      const ans = await io.question(`\n${prompt} [y/N] `);
      return /^y(es)?$/i.test(ans.trim());
    } finally {
      io.close();
    }
  }
  notify(event: { kind: string; message: string }): void {
    console.error(`[${event.kind}] ${event.message}`);
  }
}
